import { FC, useCallback, useMemo, useState } from "react";
import { Namespace, useT } from "@racoon/i18n";
import {
  Button,
  Label,
  ModalFooter,
  SearchableSelect,
  Separator,
  useModal,
  useToast,
} from "@racoon/ui";
import { ConnectProto, ContractStatus, ContractType } from "@racoon/api";
import { formatTenge } from "@racoon/lib";
import { useListContractsQuery } from "~/shared/api/contract/contract.grpc";
import { useSetPickupLocationContractsMutation } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { SectionCaption } from "~/shared/ui/section-caption";
import { useLocationSubscriptions } from "../hooks/use-location-subscriptions";
import { useSelectableCounterparties } from "../hooks/use-selectable-counterparties";
import { contractOptionLabel } from "../model/contract-label";
import { CustomerBinRow, FIELD_LABEL } from "./customer-bin-row";
import { ContractPeriod } from "./contract-period";

type Props = {
  locationId: bigint;
  /** Edit mode: the attachment to swap for another contract or to detach. */
  editingContract?: ConnectProto.Contract;
  onDone: () => void;
};

/** Body of the attach / edit BY_NORM-customer modal; opened through `useAttachByNormModal`. */
const AttachByNormContent: FC<Props> = ({ locationId, editingContract, onDone }) => {
  const t = useT();
  const { toast } = useToast();
  const { selectedCompanyId } = useSelectedCompany();
  const [orgId, setOrgId] = useState(
    editingContract ? String(editingContract.targetOrganizationId) : "",
  );
  const [contractId, setContractId] = useState(editingContract ? String(editingContract.id) : "");
  const [setPickupLocationContracts, { isLoading: isSaving }] =
    useSetPickupLocationContractsMutation();

  // The RPC replaces the whole set, so the current set is read here (cached by
  // the Participants tab) rather than snapshotted when the modal opened.
  const { pickupLocation, subscriptionContracts } = useLocationSubscriptions(locationId);
  const otherContractIds = useMemo(
    () =>
      subscriptionContracts
        .map((c) => c.id)
        .filter((id) => String(id) !== String(editingContract?.id)),
    [subscriptionContracts, editingContract],
  );

  const {
    organizations,
    selectable: selectableOrganizations,
    isLoading: isLoadingOrgs,
  } = useSelectableCounterparties(orgId);
  // While the list loads, the edited contract's own customer stands in.
  const selectedOrgName =
    organizations.find((org) => String(org.id) === orgId)?.name ??
    (orgId === String(editingContract?.targetOrganizationId)
      ? editingContract?.targetOrganization?.name
      : undefined);

  const { currentData: contractsData, isFetching: isLoadingContracts } = useListContractsQuery(
    {
      serviceProviderIds: [BigInt(selectedCompanyId ?? 0)],
      targetOrganizationIds: [BigInt(orgId || 0)],
      status: ContractStatus.CONTRACT_STATUS_ACTIVE,
      type: ContractType.CONTRACT_TYPE_BY_NORM,
      // A division serves under its legal entity's contracts.
      includeAncestors: true,
    },
    { skip: !selectedCompanyId || !orgId },
  );
  const contracts = useMemo(() => {
    const taken = new Set(otherContractIds.map(String));
    return (contractsData?.contracts ?? []).filter((c) => !taken.has(String(c.id)));
  }, [contractsData, otherContractIds]);
  // The edited contract may be missing from the ACTIVE list (archived since it was attached).
  const selectedContract =
    contracts.find((c) => String(c.id) === contractId) ??
    (contractId === String(editingContract?.id) ? editingContract : undefined);

  const contractOptions = useMemo(
    () => contracts.map((c) => ({ id: String(c.id), name: contractOptionLabel(c, t) })),
    [contracts, t],
  );

  const save = async (contractIds: bigint[], doneKey: "attached" | "saved" | "detached") => {
    try {
      await setPickupLocationContracts({ pickupLocationId: locationId, contractIds }).unwrap();
      toast({ title: t(Namespace.PickupLocation, `participants.${doneKey}`) });
      onDone();
    } catch {
      // GlobalErrorToaster already reports the API error; stay open so the user can retry.
    }
  };

  // Saving before the current set has loaded would unbind every other contract.
  const canSave = !!pickupLocation && !isSaving;

  return (
    <>
      <section className="flex flex-col gap-6 p-6">
        <SectionCaption>{t(Namespace.PickupLocation, "modal.customer")}</SectionCaption>
        <CustomerBinRow
          id="attach-by-norm-org"
          organizations={selectableOrganizations}
          value={orgId}
          onChange={(id) => {
            setOrgId(id);
            setContractId("");
          }}
          fallbackBin={editingContract?.targetOrganization?.bin}
          valueLabel={selectedOrgName}
          loading={isLoadingOrgs}
        />
      </section>
      <Separator />

      <section className="flex flex-col gap-6 p-6">
        <SectionCaption className="flex gap-2.5">
          {t(Namespace.PickupLocation, "participants.byNormContracts")}
          <span className="text-blue-600">{selectedOrgName}</span>
        </SectionCaption>
        <div className="flex flex-col gap-4">
          <SearchableSelect
            id="attach-by-norm-contract"
            size="sm"
            value={contractId}
            options={contractOptions}
            onChange={setContractId}
            valueLabel={selectedContract ? contractOptionLabel(selectedContract, t) : undefined}
            disabled={!orgId}
            loading={isLoadingContracts}
            placeholder={t(Namespace.PickupLocation, "modal.selectContract")}
            searchPlaceholder={t(Namespace.PickupLocation, "modal.searchContract")}
          />
          <div className="flex items-center gap-16">
            <ContractPeriod startAt={selectedContract?.startAt} endAt={selectedContract?.endAt} />
            <div className="flex items-center gap-2">
              <Label className={FIELD_LABEL}>
                {t(Namespace.Customers, "modal.addContract.monthlyFee")}
              </Label>
              <span className="flex h-8 items-center px-2 text-sm leading-4 tracking-[-0.01em] text-text-body">
                {selectedContract ? formatTenge(Number(selectedContract.monthlyFee ?? 0)) : "—"}
              </span>
            </div>
          </div>
        </div>
      </section>

      <ModalFooter className="!p-4">
        {editingContract && (
          <Button
            type="button"
            variant="solidRed"
            size="sm"
            className="mr-auto !text-sm"
            onClick={() => save(otherContractIds, "detached")}
            disabled={!canSave}
          >
            {t(Namespace.PickupLocation, "participants.detach")}
          </Button>
        )}
        <Button
          type="button"
          variant="solidBlue"
          size="sm"
          className="!text-sm"
          onClick={() =>
            selectedContract &&
            save([...otherContractIds, selectedContract.id], editingContract ? "saved" : "attached")
          }
          disabled={!selectedContract || !canSave}
        >
          {t(
            Namespace.PickupLocation,
            editingContract ? "participants.saveSubmit" : "participants.attachSubmit",
          )}
        </Button>
      </ModalFooter>
    </>
  );
};

/** Opens the attach modal, or with a contract, the edit-attachment modal. */
export const useAttachByNormModal = () => {
  const t = useT();
  const { openModal, closeModal } = useModal();

  return useCallback(
    (locationId: bigint, editingContract?: ConnectProto.Contract) => {
      const id = openModal({
        width: 776,
        closeButtonTop: "top-4",
        headerClassName: "!p-4",
        bodyClassName: "!p-0",
        title: (
          <span className="text-xl font-bold leading-6 tracking-[-0.01em] text-text-body">
            {t(
              Namespace.PickupLocation,
              editingContract ? "participants.editTitle" : "participants.attachTitle",
            )}
          </span>
        ),
        body: (
          <AttachByNormContent
            locationId={locationId}
            editingContract={editingContract}
            onDone={() => closeModal(id)}
          />
        ),
      });
    },
    [t, openModal, closeModal],
  );
};
